import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useLessonStore } from '@/store/lessonStore'
import { useAuthStore } from '@/store/authStore'
import { apiClient } from '@/services/api'
import { handleApiError, showSuccessToast } from '@/utils/errorHandling'
import { useAudio } from '@/hooks/useAudio'
import { audioService } from '@/services/audio'
import { useTour } from '@/components/tour/TourProvider'
import {
  Play,
  Pause,
  Square,
  Volume2,
  VolumeX,
  MoreVertical,
  AlertTriangle,
  CreditCard,
  Radio,
  BookOpen,
} from 'lucide-react'

export function LessonControls({ variant = 'header' }: { variant?: 'header' | 'center' }) {
  const { currentLesson, currentActivity, updateLessonState, setCurrentLesson, audioEnabled, setAudioEnabled } = useLessonStore()
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const creditBalance = user?.credit_balance ?? 0
  const [showMenu, setShowMenu] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showEndLessonConfirm, setShowEndLessonConfirm] = useState(false)
  const [showModeSelect, setShowModeSelect] = useState(false)
  const [showPaymentRequired, setShowPaymentRequired] = useState(false)
  const [conflictLesson, setConflictLesson] = useState<{ id: string; name: string; mode: 'live' | 'self_paced' } | null>(null)
  const { activeTourId } = useTour()
  const isOnboarding = activeTourId === 'live_class'

  useAudio()
  useEffect(() => { audioService.setEnabled(audioEnabled) }, [audioEnabled])

  const startWithMode = async (mode: 'live' | 'self_paced', force = false) => {
    if (!currentLesson?.id) return

    try {
      setIsLoading(true)
      // Update lesson mode first
      if (!force) {
        await apiClient.updateLesson(currentLesson.id, { mode })
        setCurrentLesson({ ...currentLesson, mode })
      }

      // Now start the lesson
      const response = await apiClient.startLesson(currentLesson.id, force)
      if (response.success) {
        updateLessonState({ status: 'live' })
        audioService.play('lesson-start')
        showSuccessToast('Lesson started successfully!')
      }
    } catch (error: any) {
      if (error?.response?.status === 409 && error?.response?.data?.active_lesson) {
        setConflictLesson({ ...error.response.data.active_lesson, mode })
        setShowModeSelect(false)
        return
      }
      if (error?.response?.status === 402 && error?.response?.data?.code === 'PAYMENT_METHOD_REQUIRED') {
        setShowModeSelect(false)
        setShowPaymentRequired(true)
        return
      }
      handleApiError(error, 'start lesson')
    } finally {
      setIsLoading(false)
      if (!conflictLesson) setShowModeSelect(false)
    }
  }

  const handleForceStart = async () => {
    if (!conflictLesson) return
    const mode = conflictLesson.mode
    setConflictLesson(null)
    await startWithMode(mode, true)
  }

  const handleStartLesson = () => {
    setShowModeSelect(true)
  }

  const handleEndLesson = async () => {
    if (!currentLesson?.id) return

    try {
      setIsLoading(true)
      const response = await apiClient.completeLesson(currentLesson.id)
      if (response.success) {
        updateLessonState({ status: 'completed' })
        audioService.play('lesson-end')
        showSuccessToast('Lesson ended successfully!')
      }
    } catch (error: any) {
      handleApiError(error, 'end lesson')
    } finally {
      setIsLoading(false)
      setShowEndLessonConfirm(false)
    }
  }

  const isActivityRunning = currentActivity?.status === 'active'
  const isActivityPaused = currentActivity?.status === 'paused'
  const showPauseButton = isActivityRunning || isActivityPaused

  const handlePauseResume = async () => {
    if (!currentLesson?.id || !currentActivity?.id) return
    try {
      if (isActivityRunning) {
        await apiClient.pauseActivity(currentLesson.id, currentActivity.id)
      } else {
        await apiClient.resumeActivity(currentLesson.id, currentActivity.id)
      }
    } catch {
      // pause/resume failed
    }
  }

  const isPreStart = currentLesson?.status === 'draft' || currentLesson?.status === 'ready'

  // Center variant: just the start button + modals
  if (variant === 'center') {
    return (
      <>
        {isPreStart && (
          <button
            onClick={handleStartLesson}
            disabled={isLoading}
            className="ds-btn ds-btn-primary mx-auto"
          >
            <Play className="w-5 h-5" />
            {isLoading ? 'Starting...' : 'Start Lesson'}
          </button>
        )}

        {/* Mode Selection Modal */}
        <AnimatePresence>
          {showModeSelect && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[10001] flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-surface-deep border border-theme-border rounded-xl p-6 max-w-md w-full"
              >
                <h3 className="ds-section-title mb-1">Choose Lesson Mode</h3>
                <p className="text-sm text-[var(--color-text-muted)] mb-5">How should students experience this lesson?</p>

                <div className="space-y-3 mb-5">
                  <button
                    onClick={() => startWithMode('live')}
                    disabled={isLoading}
                    className="w-full flex items-center gap-4 p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-primary)] transition-colors disabled:opacity-50 text-left"
                  >
                    <Radio className="w-6 h-6 text-[var(--color-secondary)] shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-[var(--color-text)]">Live (Instructor-Led)</div>
                      <div className="text-xs text-[var(--color-text-muted)]">You control the pace. All students see the same activity at the same time.</div>
                    </div>
                  </button>

                  {!isOnboarding && (
                    <button
                      onClick={() => startWithMode('self_paced')}
                      disabled={isLoading}
                      className="w-full flex items-center gap-4 p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-primary)] transition-colors disabled:opacity-50 text-left"
                    >
                      <BookOpen className="w-6 h-6 text-[var(--color-primary)] shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-[var(--color-text)]">Self-Paced</div>
                        <div className="text-xs text-[var(--color-text-muted)]">Students progress independently through activities at their own pace.</div>
                      </div>
                    </button>
                  )}
                </div>

                {isLoading && (
                  <div className="flex items-center justify-center gap-2 text-sm text-[var(--color-text-muted)] mb-3">
                    <div className="animate-spin w-4 h-4 border-2 border-[var(--color-primary)] border-t-transparent rounded-full" />
                    Starting lesson...
                  </div>
                )}

                {!isOnboarding && (
                  <button
                    onClick={() => setShowModeSelect(false)}
                    disabled={isLoading}
                    className="ds-btn ds-btn-secondary ds-btn-full"
                  >
                    Cancel
                  </button>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Run Conflict Modal */}
        <AnimatePresence>
          {conflictLesson && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[10001] flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-surface-deep border border-theme-border rounded-xl p-6 max-w-md w-full"
              >
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  <h3 className="text-lg font-semibold text-yellow-400">Lesson In Progress</h3>
                </div>
                <p className="text-sm text-[var(--color-text-muted)] mb-6">
                  <strong className="text-[var(--color-text)]">{conflictLesson.name}</strong> is currently running.
                  Starting this lesson will end it and disconnect any active students.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConflictLesson(null)}
                    className="ds-btn ds-btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleForceStart}
                    disabled={isLoading}
                    className="ds-btn ds-btn-danger flex-1"
                  >
                    {isLoading ? 'Starting...' : 'End & Start New'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Payment Method Required Modal */}
        <AnimatePresence>
          {showPaymentRequired && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[10001] flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-surface-deep border border-theme-border rounded-xl p-6 max-w-md w-full"
              >
                <div className="flex items-center gap-3 mb-4">
                  <CreditCard className="w-6 h-6 text-yellow-400" />
                  <h3 className="text-lg font-semibold text-[var(--color-text)]">Payment Method Required</h3>
                </div>
                <p className="text-sm text-[var(--color-text-muted)] mb-6">
                  A card on file is required before starting a paid lesson. Any tier overages will be charged automatically.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPaymentRequired(false)}
                    className="ds-btn ds-btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => navigate('/add-payment-method')}
                    className="ds-btn ds-btn-primary flex-1"
                  >
                    Add Payment Method
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </>
    )
  }

  return (
    <div className="flex items-center gap-2">
      {/* Low credit warning */}
      {!isPreStart && creditBalance > 0 && creditBalance < 5 && currentLesson?.status !== 'completed' && (
        <div className="flex items-center gap-1 px-3 py-1.5 bg-yellow-900/50 text-yellow-300 rounded-lg text-xs border border-yellow-700">
          <AlertTriangle className="w-3 h-3" />
          <span>{creditBalance} credits left</span>
        </div>
      )}

      {/* Pause/Resume activity */}
      {showPauseButton && (
        <button
          onClick={handlePauseResume}
          className={`p-2 rounded-lg transition-colors ${
            isActivityRunning
              ? 'bg-yellow-900 text-yellow-300 hover:bg-yellow-800'
              : 'bg-green-900 text-green-300 hover:bg-green-800'
          }`}
          title={isActivityRunning ? 'Pause activity' : 'Resume activity'}
        >
          {isActivityRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
      )}

      {/* Audio toggle */}
      <button
        onClick={() => setAudioEnabled(!audioEnabled)}
        className={`p-2 rounded-lg transition-colors ${
          audioEnabled
            ? 'bg-surface-hover text-theme-text hover:bg-surface-hover'
            : 'bg-red-900 text-red-300 hover:bg-red-800'
        }`}
      >
        {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>

      {/* More actions menu — hidden before lesson starts */}
      {!isPreStart && <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-2 bg-surface-hover text-theme-text rounded-lg hover:bg-surface-hover transition-colors"
        >
          <MoreVertical className="w-4 h-4" />
        </button>

        <AnimatePresence>
          {showMenu && (
            <>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute right-0 top-12 bg-surface-deep border border-theme-border rounded-lg shadow-lg z-20 min-w-[180px]"
              >
                <button
                  onClick={() => {
                    setShowEndLessonConfirm(true)
                    setShowMenu(false)
                  }}
                  className="w-full px-4 py-2 text-left text-red-400 hover:bg-surface-hover rounded-lg transition-colors flex items-center gap-2"
                >
                  <Square className="w-4 h-4" />
                  End Lesson
                </button>
              </motion.div>

              {/* Click outside to close */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
            </>
          )}
        </AnimatePresence>
      </div>}

      {/* End lesson confirmation modal */}
      <AnimatePresence>
        {showEndLessonConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-30 flex items-center justify-center"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-surface-deep border border-theme-border rounded-lg p-6 max-w-md mx-4"
              >
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  <h3 className="text-lg font-semibold">End Lesson</h3>
                </div>

                <p className="text-theme-text mb-6">
                  Are you sure you want to end this lesson? This action cannot be undone and all students will be disconnected.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowEndLessonConfirm(false)}
                    className="flex-1 px-4 py-2 bg-surface-hover text-theme-text rounded-lg hover:bg-surface-hover transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEndLesson}
                    disabled={isLoading}
                    className="ds-btn ds-btn-danger flex-1"
                  >
                    {isLoading ? 'Ending...' : 'End Lesson'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  )
}